// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import { getIsShareEntity } from "@insite/client-framework/ServerSideRendering";
import { injectCss } from "@insite/mobius/utilities";
import InjectableCss, { StyledProp } from "@insite/mobius/utilities/InjectableCss";
import MobiusStyledComponentProps from "@insite/mobius/utilities/MobiusStyledComponentProps";
import parseImageUrl from "@insite/mobius/utilities/parseImageUrl";
import { CustomAppState } from "@werner/App/Store/Reducers";
import { getSettingsCollection } from "@werner/App/Store/Selectors/SettingsSelectors";
import { getCloudinaryUrl, TransformationsOptions, xClassNames } from "@werner/XDL/Utilities";
import React, { useState } from "react";
import { connect } from "react-redux";
import styled, { css } from "styled-components";

export const mapStateToProps = (state: CustomAppState) => ({
    notFoundImagePath: getSettingsCollection(state).customSettings?.smallDefaultCategoryImage,
});

interface ImagePresentationProps {
    /** CSS string or styled-components function to be injected into this component.
     * @themable */
    css?: StyledProp<ImgProps>;
}

export type ImgProps = MobiusStyledComponentProps<
    "img",
    ImagePresentationProps & {
        /** The URL to fetch the image from. If not provided, Img renders `null`. */
        src?: string;
        /** The alternative text to display in place of the image. */
        altText?: string;
        /** Controls whether the img is lazy or eager loaded, default is eager */
        loading?: string;
        // Event handler for screen readers accessing an image modal in ProductPrimaryImage.tsx
        onKeyPress?: React.KeyboardEventHandler<HTMLImageElement>;
        /**
         * XNG-Changes
         * It's square image
         */
        squareImage?: boolean;
        /** Set fallback image when image not found */
        fallbackImageSrc?: string;
        /** Ignore fallback images when true */
        disableFallbackImage?: boolean;
        /** </xng-change> */
        transformations?: TransformationsOptions;
        /** </xng-change> */
        disableLazyLoading?: boolean;
    }
>;

export type Props = ImgProps & ReturnType<typeof mapStateToProps>;

function Img(props: Props) {
    const {
        src: srcProp,
        altText,
        loading,
        squareImage,
        fallbackImageSrc,
        disableFallbackImage,
        transformations,
        disableLazyLoading,
        ...otherProps
    } = props;

    const [loaded, setLoaded] = useState(false);

    const { cloudinaryBaseUrl } = useAppSelector(state => ({
        cloudinaryBaseUrl: getSettingsCollection(state).cloudinarySettings.mediaRootPath,
    }));

    const dimensions = useQueryStringsDimensions(srcProp || props.notFoundImagePath, {
        width: otherProps.width,
        height: otherProps.height,
    });

    const isShareEntity = getIsShareEntity();

    if (!srcProp && !props.notFoundImagePath) {
        return null;
    }
    const transformationsOptions: TransformationsOptions = {
        ...transformations,
    };
    if (transformations?.width) {
        transformationsOptions.width = transformations?.width;
    }
    const transformedImage = getCloudinaryUrl(
        srcProp || props.notFoundImagePath,
        cloudinaryBaseUrl,
        transformationsOptions,
    );
    const classNames = xClassNames.append(["lazyload"], otherProps.className);

    const onLoadHandler = () => {
        setLoaded(true);
    };

    // XNG-Changes
    if (isShareEntity || disableLazyLoading) {
        return (
            <StyledImg
                {...otherProps}
                loading={loading || "lazy"}
                alt={altText}
                width={dimensions.width || otherProps.width}
                height={dimensions.height || otherProps.height}
                src={transformedImage}
            />
        );
    }

    // XNG-Changes
    return (
        <StyledImg
            {...otherProps}
            loading={loading || "lazy"}
            alt={altText}
            onLoad={onLoadHandler}
            onError={onLoadHandler}
            width={dimensions.width || otherProps.width}
            height={dimensions.height || otherProps.height}
            className={classNames}
            {...{ "data-src": transformedImage }}
            {...(loaded
                ? {}
                : {
                      css: css`
                          visibility: hidden;
                      `,
                  })}
        />
    );
}

const useQueryStringsDimensions = (
    src?: string,
    styleProps?: { width?: string | number; height?: string | number },
) => {
    return React.useMemo(() => {
        const queryStrings = src && /\?.*/.exec(src);
        const noDimensions = {
            width: styleProps?.width || "",
            height: styleProps?.height || "",
        };

        // XNG-Changes: give width/height that pass by parameter height priority
        if (styleProps?.width || styleProps?.height) {
            return noDimensions;
        }

        if (queryStrings) {
            const parsedUrl  = parseImageUrl(src);

            if (!parsedUrl.width || !parsedUrl.height) {
                return noDimensions;
            }

            return parsedUrl;
        }
        return noDimensions;
    }, [src]);
};

const StyledImg = styled.img<InjectableCss>`
    ${injectCss}
`;

export default connect(mapStateToProps)(Img);
